import React, { useCallback, useContext, useEffect, useMemo } from 'react';
import { message } from 'antd';
import { v4 as uuid } from 'uuid';
import { withTranslation } from 'react-i18next';
import { TabMenuItemContainer } from '../../SidebarMenu/styles';
import { BusinessLogicButton, RulesTabContainer } from './styles';
import { BUSINESS_LOGIC_TAB } from '../translationValues';
import BusinessLogicTable from './businessLogicTable';
import {
  COMPLEX_DOCUMENT_CONTEXT_KEYS,
  useDocumentContext,
} from '../../../context/complexDocumentContext';
import { BusinessLogic, FLOW_HINTS } from '../../../context/businessLogicContext';
import { getBusinessLogics, getEmailBusinessLogics } from '../../../utils/services/businessLogicApi';
import { CONTEXT_KEYS, useArtboardContext } from '../../../context/complexArtboardContext';
import { CREATIVE_BLOCK } from '../../../utils/complexDocumentConstants';
import { useVersionContext, VERSION_CONTEXT_KEYS } from '../../../context/complexVersionContext';

const NAMESPACES = ['BUSINESS_LOGIC'];
const EXCLUDED_VARIABLE_TYPES = ['ListString', 'ListDate'];

export const OPERATOR_VALUES = {
  EQUALS: 'Equals',
  NOT_EQUALS: 'Does not equal',
  EQUALS_ANY_OF: 'Equals any of',
  IS_EMPTY: 'Is empty',
  GREATER_THAN: 'Greater than',
  GREATER_THAN_OR_EQUAL: 'Greater than or equal to',
  LESSER_THAN: 'Less than',
  LESSER_THAN_OR_EQUAL: 'Less than or equal to',
  STARTS_WITH: 'Starts with',
  DOES_NOT_START_WITH: 'Does not start with',
  CONTAINS: 'Contains',
  DOES_NOT_CONTAIN: 'Does not contain'
};

export const OPERATOR_SYMBOLS = {
  Equals: '==',
  'Does not equal': '!=',
  'Equals any of': '=~',
  'Greater than': '>',
  'Greater than or equal to': '>=',
  'Less than': '<',
  'Less than or equal to': '<=',
  'Starts with': '=^',
  'Contains': '==~',
  'Does not contain': '!~'
};

export const MATCHERS = {
  matchAll: 'ALL',
  matchfirst: 'FIRST',
  matchLast: 'LAST',
};

export const EQUALS_OPERATORS = [
  OPERATOR_VALUES.EQUALS,
  OPERATOR_VALUES.NOT_EQUALS
];

export const STARTS_OR_CONTAINS_OPERATOR = [
  OPERATOR_VALUES.STARTS_WITH,
  OPERATOR_VALUES.DOES_NOT_START_WITH,
  OPERATOR_VALUES.CONTAINS,
  OPERATOR_VALUES.DOES_NOT_CONTAIN
]

export const OPERATOR_KEY = {
  'Starts with': 'startsWith',
  'Contains': 'contains',
  'Does not contain': 'contains'
}

export const getDefaultVariable = () => {
  return {
    variableId: uuid(),
    type: '',
    isNew: true,
  };
};

export const getDefaultExtraCondition = (objectName) => {
  return {
    id: uuid(),
    operator: OPERATOR_VALUES.EQUALS,
    value: '',
    objectName: objectName,
  };
};

export const getDefaultCondition = (variableId = '', objectName) => {
  return {
    id: uuid(),
    operator: OPERATOR_VALUES.EQUALS,
    value: '',
    variableId: variableId,
    isNew: true,
    extraConditions: [],
    objectName: objectName,
  };
};

export const getDefaultRule = defaultConditions => {
  return {
    id: uuid(),
    conditions: defaultConditions || [getDefaultCondition()],
    blockId: '',
    isNew: true,
    ruleText: '',
    expression: '',
    isStandalone: false,
  };
};

const newBusinessLogic = (name, documentId) => {
  return {
    name: name,
    id: uuid(),
    documentId,
    type: 'block',
    predicateTruth: MATCHERS.matchAll,
    rules: [getDefaultRule()],
    variables: [getDefaultVariable()],
  };
};

export const getAllBlocks = (documentContext, isGrouping= false) => {
  let allBlocks = [];
  let allGroups = [];
  const allBlockVariableContracts = [];
  documentContext[COMPLEX_DOCUMENT_CONTEXT_KEYS.FLOWS].forEach(flowUUID => {
    const contextFlow = documentContext[flowUUID];

    let blockIds = [];
    blockIds = [
      ...blockIds,
      ...(isGrouping && contextFlow.header ? [contextFlow.header] : []),
      ...(isGrouping && contextFlow.footer ? [contextFlow.footer] : []),
      ...contextFlow.pages.reduce((acc, pageId) => {
        return [...acc, ...contextFlow[pageId]];
      }, []),
    ];

    allBlocks = [
      ...allBlocks,
      ...blockIds
        .flatMap(blockUUID => {
          const blockDetails = documentContext[blockUUID];
          allBlockVariableContracts.push(...(blockDetails.variableContract || []));
          if (blockDetails?.isStandalone && blockDetails.type === 'parent') {
            return blockDetails?.contents?.map(childBlockUUID => {
              return {
                ...documentContext[childBlockUUID],
                backendId: documentContext['backendIds'][childBlockUUID],
              };
            });
          }
          return {
            ...blockDetails,
            backendId: documentContext['backendIds'][blockUUID],
          };
        })
        .filter(block => block.blockName),
    ];
    allGroups = [
      ...allGroups,
      ...blockIds
        .flatMap(blockUUID => {
          const blockDetails = documentContext[blockUUID];
          if (blockDetails?.blockType === CREATIVE_BLOCK && Array.isArray(blockDetails.groups)) {
            const groups=[];
            blockDetails.groups.forEach(g => {
              groups.push({
                blockUUID: blockDetails.uuid,
                groupId: g.groupId,
                groupName: g.groupName,
                flowUUID: flowUUID
              });
            });
            return groups;
          }
        })
    ]
  });

  const allBlockVariableContractsWithoutArray = allBlockVariableContracts.filter(v => v.type === 'groupObject' || (v.type === 'object' && !v.isArray)).map(({ name, type }) => ({ name, type }));
  
  return {allBlocks, allGroups, allBlockVariableContractsWithoutArray}; 
};

const getDiffBlocks = (allDocumentBlocks = [], businessLogics = []) => {
  const usedBls = businessLogics.reduce((acc, currValue) => [...acc, ...currValue.rules], []);

  return allDocumentBlocks.filter(
    // eslint-disable-next-line eqeqeq
    block => !usedBls.find(bussblock => bussblock.blockId == block.backendId),
  );
};

const BusinessLogicTab = ({ t, editable }) => {
  const {
    currentBusinessLogic,
    businessLogics,
    flowHints,
    isAddBusinessLogic,
    setCurrentBusinessLogic,
    setBusinessLogics,
    setFlowHints,
    setAddBusinessLogic,
  } = useContext(BusinessLogic);

  const contextSelector = useCallback(store => store, []);
  const [documentContext] = useDocumentContext(contextSelector);

  const interactiveDesignSelector = useCallback(store => store[CONTEXT_KEYS.IS_INTERACTIVE_DESIGN], []);
  const [isInteractiveDesign] = useArtboardContext(interactiveDesignSelector, `BusinessLogicTab`);

  const documentId = documentContext[COMPLEX_DOCUMENT_CONTEXT_KEYS.ID];
  const {allBlocks, allBlockVariableContractsWithoutArray} = useMemo(() => getAllBlocks(documentContext), []);
  const currBussLogicList = currentBusinessLogic ? [currentBusinessLogic] : [];
  const diffBlocks = getDiffBlocks(allBlocks, [...businessLogics, ...currBussLogicList]);

  const artboardSelector = useCallback(
    store => ({
      globalVariables: store[COMPLEX_DOCUMENT_CONTEXT_KEYS.GLOBAL_VARIABLES],
      isEmailTemplate: store[COMPLEX_DOCUMENT_CONTEXT_KEYS.IS_EMAIL_TEMPLATE],
    }),
    [],
  );
  const [{ globalVariables, isEmailTemplate }] = useDocumentContext(artboardSelector);
  const versionSettingsVisibleSelector = useCallback(
    store => store[VERSION_CONTEXT_KEYS.VERSION_SETTINGS_VISIBLE],
    [],
  );
  const [versionSettingsVisible] = useVersionContext(versionSettingsVisibleSelector);
  const gVariablesWithoutList = useMemo(
    () => globalVariables.filter(gVariable => !EXCLUDED_VARIABLE_TYPES.includes(gVariable.type)),
    [globalVariables],
  );

  // It is a newFlow is no business logic is available and if the user has not selected any variable for the rule
  // before leaving to other tabs.
  const isNewFlow = businessLogics.length === 0 && !currentBusinessLogic?.variables?.some(bVar => !!bVar?.type);

  const fetchBusinessLogics = async () => {
    if (versionSettingsVisible) return;
    let getBusinessLogicsApi = getBusinessLogics;
    if (isEmailTemplate) {
      getBusinessLogicsApi = getEmailBusinessLogics;
    }
    try {
      const response = await getBusinessLogicsApi(documentId);
      if (response && response.statusCode === 1803 && response.data) {
        setBusinessLogics(response.data);
      }
    } catch (e) {
      console.log(e);
    }
  };

  useEffect(() => {
    fetchBusinessLogics();
  }, []);

  const saveBusinessLogic = businessLogic => {
    setCurrentBusinessLogic(null);
    setFlowHints({ ...flowHints, ...FLOW_HINTS });
    const bl = businessLogics.find(item => item.id === businessLogic.id);
    if (bl) {
      setBusinessLogics(
        businessLogics.map(item => {
          if (item.id === businessLogic.id) {
            item = { ...item, ...businessLogic };
          }
          return item;
        }),
      );
    } else {
      setBusinessLogics([...businessLogics, businessLogic]);
    }
  };

  const removeBusinessLogic = businessLogic => {
    setBusinessLogics(businessLogics.filter(item => item.id !== businessLogic.id));
  };

  const updateFlowHints = obj => {
    setFlowHints({ ...flowHints, ...obj });
  };

  const isValidAndUniqueName = (name, businessLogicId) => {
    const isValid = !!name;
    const isUnique = !businessLogics.find(
      item => item.name === name && item.id !== businessLogicId,
    );
    return isValid && isUnique;
  };

  const saveName = name => {
    if (!isValidAndUniqueName(name)) {
      message.error(
        t(BUSINESS_LOGIC_TAB.UNIQUE_NAME_ERROR.key, BUSINESS_LOGIC_TAB.UNIQUE_NAME_ERROR.value),
      );
    } else {
      const businessLogic = newBusinessLogic(name, documentId);
      setAddBusinessLogic(false);
      setCurrentBusinessLogic(businessLogic);
    }
  };

  const addBusinessLogic = () => {
    if (!currentBusinessLogic) {
      setAddBusinessLogic(true);
    } else {
      message.error(
        t(
          BUSINESS_LOGIC_TAB.SAVE_PREVIOUS_BL_ERROR.key,
          BUSINESS_LOGIC_TAB.SAVE_PREVIOUS_BL_ERROR.value,
        ),
      );
    }
  };

  return (
    <TabMenuItemContainer columnDirection={false} justifyContent="left" disabled={!editable}>
      <RulesTabContainer>
        {editable && !isInteractiveDesign && (
          <BusinessLogicButton>
            <button
              id="add-business-logic"
              onClick={() => addBusinessLogic(true)}
              shape="round"
              data-hover={t(
                BUSINESS_LOGIC_TAB.ADD_NEW_BUSINESS_LOGIC.key,
                BUSINESS_LOGIC_TAB.ADD_NEW_BUSINESS_LOGIC.value,
              )}
            >
              <div>
                {t(
                  BUSINESS_LOGIC_TAB.ADD_NEW_BUSINESS_LOGIC.key,
                  BUSINESS_LOGIC_TAB.ADD_NEW_BUSINESS_LOGIC.value,
                )}
              </div>
            </button>
          </BusinessLogicButton>
        )}
        {!!isAddBusinessLogic || !!currentBusinessLogic ? (
          <BusinessLogicTable
            businessLogic={currentBusinessLogic || newBusinessLogic()}
            t={t}
            isNewFlow={isNewFlow}
            flowHints={flowHints}
            isEditMode={true}
            allBlocks={allBlocks}
            diffBlocks={diffBlocks}
            isAddBusinessLogic={isAddBusinessLogic}
            globalVariables={gVariablesWithoutList}
            blockVariableContracts={allBlockVariableContractsWithoutArray}
            updateFlowHints={updateFlowHints}
            saveBusinessLogic={saveBusinessLogic}
            isValidAndUniqueName={isValidAndUniqueName}
            setCurrentBusinessLogic={setCurrentBusinessLogic}
            saveName={saveName}
          />
        ) : (
          <></>
        )}
        {[...businessLogics].reverse().map((businessLogicItem, index) => (
          <BusinessLogicTable
            businessLogic={businessLogicItem}
            t={t}
            isNewFlow={false}
            editable={editable}
            isInteractiveDesign={isInteractiveDesign}
            flowHints={flowHints}
            globalVariables={gVariablesWithoutList}
            blockVariableContracts={allBlockVariableContractsWithoutArray}
            key={businessLogicItem.name}
            allBlocks={allBlocks}
            diffBlocks={diffBlocks}
            updateFlowHints={updateFlowHints}
            saveBusinessLogic={saveBusinessLogic}
            isValidAndUniqueName={isValidAndUniqueName}
            removeBusinessLogic={removeBusinessLogic}
          />
        ))}
      </RulesTabContainer>
    </TabMenuItemContainer>
  );
};

export default withTranslation(NAMESPACES)(BusinessLogicTab);
